<!-- 首頁: 投資流程與風控 -->
<template>
  <!-- 使用到 new URL() 的元件不能使用 SSR -->
  <ClientOnly>
    <div class="risk-graphic">
      <div class="strategy-grid">
        <article v-for="card in strategyCards" :key="card.title" class="strategy-card">
          <div class="card-top">
            <p class="tagline font-caption">{{ card.tagline }}</p>
            <div class="icon-box">
              <img :src="card.image" :alt="card.title">
            </div>
          </div>
          <div class="card-bottom">
            <h5 class="bold">{{ card.title }}</h5>
            <p class="description font-body">{{ card.description }}</p>
          </div>
        </article>
      </div>

      <div class="governance">
        <component :is='deviceView.desktop ? "h4" : "h3"' class="title bold" :class="deviceView.mobile ? 'text-center' : ''">
          {{ governanceTitle }}
        </component>
        <article class="governance-card">
          <div class="card-top">
            <p class="tagline font-caption">{{ governanceCard.tagline }}</p>
            <div class="icon-box">
              <img :src="governanceCard.image" :alt="governanceCard.title">
            </div>
          </div>
          <div class="card-bottom">
            <h5 class="bold">{{ governanceCard.title }}</h5>
            <p class="description font-body">{{ governanceCard.description }}</p>
          </div>
        </article>
      </div>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
const deviceView = useDeviceView()
const { english: enMode } = useViewMode()

const strategyCards = computed(() => enMode.value ? [
  {
    title: ' Fundamental Research',
    tagline: 'Industry Verification · Frontline Insights · Supply Chain Synergy',
    description: 'Leveraging tech expertise and supply chain verification to transform frontline insights into forward-looking research.',
    image: new URL('~/assets/images/risk-icon/method-process.png', import.meta.url).href,
  },
  {
    title: 'Quant Analysis',
    tagline: 'Cycle Pivot Prediction · High-Frequency Verification · ML Backtesting',
    description: 'Utilizing machine learning and proprietary high-frequency data to identify cycle pivots and systematically verify investment hypotheses.',
    image: new URL('~/assets/images/risk-icon/method-quant.png', import.meta.url).href,
  },
  {
    title: 'In-Depth Financial Analysis',
    tagline: 'Footnote Scrutiny · Accounting Standardization · Anti-Fraud Auditing',
    description: 'Independent CPA utilizes proprietary systems to standardize financials and scrutinize footnotes, rigorously mitigating default and fraud risks.',
    image: new URL('~/assets/images/risk-icon/method-audit.png', import.meta.url).href,
  },
] : [
  {
    title: '產業基本面研究',
    tagline: '#產業實證 #第一線洞察 #供應鏈協同',
    description: '結合數十年的科技產業經驗，透過深度的實地調研與供應鏈交叉驗證，將前線洞察轉化為具備前瞻性的研究觀點。',
    image: new URL('~/assets/images/risk-icon/method-process.png', import.meta.url).href,
  },
  {
    title: '量化系統分析',
    tagline: '#週期拐點預測 #高頻數據驗證 #機器學習回測',
    description: '運用機器學習與獨門高頻數據進行深度回測，精準識別產業週期拐點，以系統化模型驗證並挑戰投資假設。',
    image: new URL('~/assets/images/risk-icon/method-quant.png', import.meta.url).href,
  },
  {
    title: '深度財務稽核',
    tagline: '#附註穿透審查 #準則標準化 #零容忍風控',
    description: '由獨立CPA團隊透過自研系統標準化財報差異，並穿透附註深度辨識潛在財務盲點，嚴格規避違約與欺詐風險。',
    image: new URL('~/assets/images/risk-icon/method-audit.png', import.meta.url).href,
  },
])

const governanceTitle = computed(() => enMode.value ? 'Professional Risk Governance' : '專業風險治理')

const governanceCard = computed(() => enMode.value ? {
  title: 'Multi-Layered Compliance and Stability',
  tagline: 'Compliance Governance · Liquidity Stress Testing · Gatekeeper Mechanism',
  description: 'A three-tiered system aligning compliance, portfolio resilience, and security audits with global standards to ensure stability via rigorous stress testing.',
  image: new URL('~/assets/images/risk-icon/method-governance.png', import.meta.url).href,
} : {
  title: '多層次的合規與穩健保障',
  tagline: '#合規治理 #流動性壓力測試 #專業守門人機制',
  description: '建立涵蓋合規監督、投資組合韌性與個股稽核的三層監控體系，嚴格遵循國際法規標準，並透過極端市場壓力測試確保流動性安全性。',
  image: new URL('~/assets/images/risk-icon/method-governance.png', import.meta.url).href,
})
</script>

<style lang="stylus" scoped>
.risk-graphic
  width 100%
  margin 24px auto 0
.strategy-grid
  display grid
  grid-template-columns repeat(3, minmax(0, 1fr))
  gap 24px
  +under(md)
    grid-template-columns 1fr
    gap 16px
.strategy-card, .governance-card
  border-radius 8px
  box-sizing border-box
  color #24272d
  overflow hidden
.strategy-card
  background #EFEBE9
  min-height 294px
  padding 16px
  display flex
  flex-direction column
  gap 28px
  +under(md)
    min-height 0
.card-top
  display flex
  flex-direction column
  gap 16px
  align-items flex-start
  justify-content center
.card-bottom
  display flex
  flex-direction column
  align-items flex-start
  width 100%
.tagline
  min-height 40px
  line-height 20px
  display -webkit-box
  -webkit-line-clamp 2
  -webkit-box-orient vertical
  color $black-0-42
  margin 0
  word-break keep-all
  overflow-wrap anywhere
  overflow hidden
.icon-box
  width 120px
  height 120px
  display flex
  align-items center
  justify-content center
  img
    max-width 100%
    max-height 100%
    object-fit contain
h5
  padding-bottom 16px
  margin 0
  border-bottom 1px solid $black-0-12
  width 100%
.description
  color $black-0-42
  max-width 600px
  margin 16px 0 0
.governance
  margin-top 96px
  +under(md)
    margin-top 48px
  .title
    margin-bottom 24px
.governance-card
  min-height 278px
  padding 16px
  background #eceff1
  display flex
  flex-direction column
  gap 24px
  +under(md)
    min-height 0
</style>
